<template>
  <div>
    <my-header></my-header>

    <div
      v-if="isLoadedNormal"
      class="content-container-level-1"
    >
      <h2>New win streak event</h2>

      <form @submit.prevent="createWinStreaksWithConfirmation">

        <table width="100%" class="moment-table">
          <tr width="100%">
            <th width="0%">
              <label for="type-selector">Type</label>
            </th>
            <td colspan="2" width="100%">
              <b-form-select
                id="type-selector"
                v-model="type"
                :options="typeOptions"
              />
            </td>
          </tr>
          <tr class="audience" width="100%">
            <th width="0%">
              <label for="audience-selector">Audience</label>
            </th>
            <td colspan="2" width="100%">
              <b-form-select
                id="audience-selector"
                v-model="audienceId"
                :options="audienceOptions"
              />
            </td>
          </tr>
          <tr class="start" width="100%">
            <th width="0%">
              <label for="start-date">Begin</label>
            </th>
            <td width="70%" class="date">
              <b-form-datepicker
                id="start-date"
                v-model="plannedStartDate"
                :min="minPlannedStartDate"
                :start-weekday="MONDAY"
              />
            </td>
            <td width="30%">
              <time-input
                width="100%"
                v-model="plannedStartTime"
              />
            </td>
          </tr>
          <tr>
            <th>
              <label for="finish-date">End</label>
            </th>
            <td class="date">
              <b-form-datepicker
                id="finish-date"
                v-model="plannedFinishDate"
                :min="minPlannedFinishDate"
                :start-weekday="MONDAY"
              />
            </td>
            <td>
              <time-input v-model="plannedFinishTime"/>
            </td>
          </tr>
          <tr>
            <th>Duration</th>
            <td colspan="2" class="event-duration">{{durationStr}}</td>
          </tr>
        </table>

        <hr>

        <h5>Client min version</h5>
        <table class="client-min-version-range mb-2">
          <tr>
            <td>Max value (server version):</td>
            <td>{{newWinStreakInfo.gameVersion}}</td>
          </tr>
          <tr>
            <td>Min value (forced version):</td>
            <td>{{newWinStreakInfo.forcedVersion}}</td>
          </tr>
        </table>
        <p class="mb-2">
          {{clientMinVersionComment}}
        </p>
        <input class="form-control" v-model="clientMinVersion" required>

        <hr>

        <h5>
          <b-form-checkbox v-model="isRepeatRequired">
            Повторять по правилам
          </b-form-checkbox>
        </h5>

        <template v-if="isRepeatRequired">
          <div class="mb-3">
            <label for="pause" class="mb-1">Пауза между событиями (в часах)</label>
            <input id="pause" type="number" min="1" class="form-control" v-model="pauseHourStr">
          </div>

          <p class="mb-1">Повторять до даты</p>
          <moment-input-table
            v-model="repeatEndMoment"
            :min="minRepeatEndDateStr"
            class="mb-3"
          />

          <template v-if="optionalPeriods !== null">
            <p class="mb-1">Все события, которые будут созданы.</p>
            <periods-table
              :periods="optionalPeriods"
            />
          </template>

        </template>

        <hr>

        <b-button :to="{name: 'win_streaks'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>


    </div>

    <loading-indicator
      v-if="isLoading"
    />
  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

/**
 * @typedef { import('../util.js').Period } Period
 */
import { convertMomentToDataStr              } from '../util.js';
import { convertMomentToTimeStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { normalizeVersionStr                 } from '../util.js';
import { getAudienceOptions                  } from '../util.js';
import { convertMomentToStamp                } from '../util.js';
import { createRealPeriod                    } from '../util.js';
import { convertHourToSec                    } from '../util.js';
import { parseUint32                         } from '../util.js';
import { getRepeatedPeriods                  } from '../util.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput from './TimeInput.vue';
import PeriodsTable from './PeriodsTable.vue';
import MomentInputTable from './util/MomentInputTable.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'WinStreakAdd',

  components: {
    MyHeader,
    LoadingIndicator,
    TimeInput,
    PeriodsTable,
    MomentInputTable,
  },

  data() {
    const start = new Date();
    const startDateStr = convertMomentToDataStr(start);
    const startTimeStr = convertMomentToTimeStr(start);

    // By default win streak event duration is 60 hours (2 days 12 hours).
    const DEFAULT_DURATION_HOURS = 60;

    const finish = new Date(start.getTime());
    finish.setHours(finish.getHours() + DEFAULT_DURATION_HOURS);
    const finishDateStr = convertMomentToDataStr(finish);
    const finishTimeStr = convertMomentToTimeStr(finish);

    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,
      newWinStreakInfo: null,

      /** @type {?number} */
      type: null,

      typeOptions: null,

      audienceId: 0,

      plannedStartDate:     startDateStr,
      minPlannedStartDate:  startDateStr,
      plannedStartTime:     startTimeStr,

      plannedFinishDate:    finishDateStr,
      minPlannedFinishDate: startDateStr,
      plannedFinishTime:    finishTimeStr,

      clientMinVersion: '',

      isRepeatRequired: false,
      pauseHourStr: '24',

      repeatEndMoment:     finish,
      minRepeatEndDateStr: startDateStr,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },
    
    /** @returns {Object[]} */
    audienceOptions() {
      return getAudienceOptions();
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDate, this.plannedStartTime);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDate, this.plannedFinishTime);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {[?number, ?Error]} */
    repeatEndStamp() {
      const repeatEndMoment = this.repeatEndMoment;
      if(repeatEndMoment === null) {
        return [null, new Error('Repeat end moment is invalid.')];
      }

      const stamp = convertMomentToStamp(repeatEndMoment);
      return [stamp, null];
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const newWinStreakInfo = this.newWinStreakInfo;
      if(newWinStreakInfo === null) {
        return null;
      }

      if(newWinStreakInfo.gameVersion === newWinStreakInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new win streak event must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + newWinStreakInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new win streak event inside this range.';
    },

    /** @returns {[?Period, ?Error]} */
    firstPeriod() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return [null, new Error('Start moment is not specified.')];
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return [null, new Error('Finish moment is not specified.')];
      }

      const plannedStartSec  = convertMomentToStamp(plannedStartMoment);
      const plannedFinishSec = convertMomentToStamp(plannedFinishMoment);

      return createRealPeriod(plannedStartSec, plannedFinishSec);
    },

    /** @returns {[?number, ?Error]} */
    pauseHour() {
      const pauseHour = parseUint32(this.pauseHourStr);
      if(pauseHour === null) {
        return [null, new Error('Pause between events is invalid.')];
      }
      return [pauseHour, null];
    },

    /** @returns {[?number, ?Error]} */
    pauseSec() {
      const [pauseHour, err] = this.pauseHour;
      if(err !== null) {
        return [null, err];
      }
      if(pauseHour === null) {
        return [null, new Error('pauseHour === null')];
      }

      const pauseSec = convertHourToSec(pauseHour);
      return [pauseSec, null];
    },

    /** @returns {[?Period[], ?Error]} */
    periods() {
      /** @type {?Period} */
      let firstPeriod;

      /** @type {?Error} */
      let err;

      [firstPeriod, err] = this.firstPeriod;
      if(err !== null) {
        return [null, err];
      }
      if(firstPeriod === null) {
        return [null, new Error('firstPeriod === null')];
      }

      if(!this.isRepeatRequired) {
        const periods = [firstPeriod];
        return [periods, null];
      }

      /** @type {?number} */
      let pauseSec;
      [pauseSec, err] = this.pauseSec;
      if(err !== null) {
        return [null, err];
      }
      if(pauseSec === null) {
        return [null, new Error('pauseSec === null')];
      }

      /** @type {?number} */
      let repeatEndStamp;
      [repeatEndStamp, err] = this.repeatEndStamp;
      if(err !== null) {
        return [null, err];
      }
      if(repeatEndStamp === null) {
        return [null, new Error('repeatEndStamp === null')];
      }

      if(firstPeriod.end > repeatEndStamp) {
        return [null, new Error('First period end must be before repeat end.')];
      }

      const periods = getRepeatedPeriods(firstPeriod, pauseSec, repeatEndStamp);
      return [periods, null];
    },

    /** @returns {?Period[]} */
    optionalPeriods() {
      const [periods, err] = this.periods;
      if(err !== null) {
        return null;
      }
      return periods;
    },
  },

  created() {
    this.loadNewWinStreakInfo();
  },

  methods: {
    loadNewWinStreakInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/new_win_streak_info')
      .then(response => {
        /** @type {NewWinStreakInfo} */
        const data = response.data.data;

        const gameVersion = normalizeVersionStr(data.gameVersion);
        const forcedVersion = data.forcedVersion;

        this.typeOptions = getTypeOptions(data.winStreakTypes);
        if(this.typeOptions.length > 0) {
          this.type = this.typeOptions[0].value;
        }

        this.newWinStreakInfo = {
          gameVersion,
          forcedVersion,
        };

        this.clientMinVersion = forcedVersion;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    createWinStreaksWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const [winStreaksDef, err] = this.getWinStreaksDef();
      if(err !== null) {
        notifier.showError(err, {sender: this});
        return;
      }
      if(winStreaksDef === null) {
        notifier.showError(new Error('winStreaksDef === null'), {sender: this});
        return;
      }

      const eventAmount = winStreaksDef.periods.length;
      this.confirmWinStreaksCreation(eventAmount, () => {
        this.createWinStreaks(winStreaksDef);
      });
    },


    /**
     * @returns {[?WinStreaksDef, ?Error]}
     */
    getWinStreaksDef() {
      const type = this.type;
      if(type === null) {
        return [null, new Error('Type must be specied.')];
      }

      const trimmedClientMinVersion = this.clientMinVersion.trim();
      if(trimmedClientMinVersion.length === 0) {
        return [null, new Error('Client min version must be specified.')];
      }

      const [periods, err] = this.periods;
      if(err !== null) {
        return [null, err];
      }
      if(periods === null) {
        return [null, new Error('periods === null')];
      }

      const winStreaksDef = new WinStreaksDef(type, this.audienceId, trimmedClientMinVersion, periods);
      return [winStreaksDef, null];
    },

    /**
     * @param {number} eventAmount
     * @param {ConfirmHandle} onConfirmed
     */
    confirmWinStreaksCreation(eventAmount, onConfirmed) {
      const eventWord = (eventAmount === 1) ? 'event' : 'events' ;
      confirmAction(
        this,
        `Are you sure you want to create this win streak ${eventWord} {env}?`,
        'Create',
        onConfirmed,
      );
    },

    //-------------------------------------------------------------------------

    /**
     * @param {WinStreaksDef} winStreaksDef
     */
    createWinStreaks(winStreaksDef) {
      HTTP.post('/win_streaks/add', winStreaksDef)
      .then(response => {
        const result = response.data.result;

        const RESULT_SUCCESS = 1;
        if(result.code === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Win streak event created.');
          this.$router.push({name: 'win_streaks'});
          return;
        }

        notifier.showError(result.message, {sender: this});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};


/**
 * @typedef {Object} NewWinStreakInfo
 * @property {WinStreakType[]} winStreakTypes
 * @property {string} gameVersion
 * @property {string} forcedVersion
 */

/**
 * @typedef {Object} WinStreakType
 * @property {number} id
 * @property {string} name
 */

// options for component b-form-select
function getTypeOptions(typeList) {
  const options = [];

  for(const t of typeList) {
    const option = {
      value: t.id,
      text: t.name,
    };
    options.push(option);
  }

  return options;
}


class WinStreaksDef {
  /**
   * @param {number} type
   * @param {number} audienceId
   * @param {string} clientMinVersion
   * @param {Period[]} periods
   */
  constructor(
    type,
    audienceId,
    clientMinVersion,
    periods,
  ) {
    this.type             = type;
    this.audienceId       = audienceId;
    this.clientMinVersion = clientMinVersion;
    this.periods          = periods;
  }
}
</script>

<style scoped>
.moment-table > tr > th {
  padding-right: .5rem;
}

.moment-table > tr > th > label {
  margin-bottom: 0;
}

.moment-table > tr > td.date {
  padding-right: .2rem;
}

.moment-table > tr:not(:last-child) > th,
.moment-table > tr:not(:last-child) > td {
  padding-bottom: .6rem;
}

.moment-table > tr.start > th,
.moment-table > tr.start > td,
.moment-table > tr.finish > th,
.moment-table > tr.finish > td {
  padding-bottom: .34rem;
}

.event-duration {
  padding-left: 0.8rem;
}

.client-min-version-range td:first-child {
  padding-right: 0.3rem;
}
</style>
